import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import { submitContact } from '../api';

const wrap = { maxWidth: 720, margin: '0 auto', padding: '40px 20px 60px', color: 'var(--text)' };
const h1 = { fontSize: 28, fontWeight: 800, marginBottom: 8 };
const p = { fontSize: 15, lineHeight: 1.7, color: 'var(--muted)', margin: '0 0 12px' };
const label = { display: 'block', fontSize: 13, color: 'var(--muted)', margin: '14px 0 6px', fontWeight: 600 };
const inputBase = {
  width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14,
  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)',
};

export default function ContactPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: user?.username || '',
    email: user?.email || '',
    subject: '',
    message: '',
    website: '', // honeypot — hidden from users, bots fill it
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(''); // '', 'sending', 'sent'
  const [errorMsg, setErrorMsg] = useState('');

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setErrorMsg('');
    const nextErrors = {};
    if (!form.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
      nextErrors.email = 'Please enter a valid email.';
    }
    if (!form.message.trim()) nextErrors.message = 'Please enter a message.';
    if (Object.keys(nextErrors).length) { setErrors(nextErrors); return; }

    setStatus('sending');
    try {
      await submitContact(form);
      setStatus('sent');
    } catch (err) {
      setStatus('');
      const data = err?.response?.data;
      if (data?.errors) setErrors(data.errors);
      setErrorMsg(data?.detail || 'Something went wrong. Please try again.');
    }
  };

  return (
    <div style={wrap}>
      <Helmet>
        <title>Contact Us — Algovex</title>
        <meta name="description" content="Get in touch with the Algovex team." />
      </Helmet>
      <h1 style={h1}>Contact Us</h1>
      <p style={p}>Questions, feedback, or need a hand? Send us a message and we’ll get back to you.</p>

      {status === 'sent' ? (
        <div style={{
          background: 'rgba(47,214,195,0.1)', border: '1px solid var(--teal)',
          borderRadius: 10, padding: 20, marginTop: 16,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>✅ Message sent</div>
          <div style={{ fontSize: 14, color: 'var(--muted)' }}>
            Thanks for reaching out — we’ll reply to <strong>{form.email}</strong> soon.
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={label}>Name</label>
              <input style={inputBase} value={form.name} onChange={set('name')} placeholder="Your name" />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={label}>Email <span style={{ color: 'var(--red)' }}>*</span></label>
              <input
                style={errors.email ? { ...inputBase, borderColor: 'var(--red)' } : inputBase}
                value={form.email} onChange={set('email')} placeholder="you@example.com" type="email"
              />
              {errors.email && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{errors.email}</div>}
            </div>
          </div>

          <label style={label}>Subject</label>
          <input style={inputBase} value={form.subject} onChange={set('subject')} placeholder="What’s this about?" />

          <label style={label}>Message <span style={{ color: 'var(--red)' }}>*</span></label>
          <textarea
            style={errors.message ? { ...inputBase, borderColor: 'var(--red)', minHeight: 140, resize: 'vertical' } : { ...inputBase, minHeight: 140, resize: 'vertical' }}
            value={form.message} onChange={set('message')} placeholder="How can we help?"
            maxLength={5000}
          />
          {errors.message && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 4 }}>{errors.message}</div>}

          {/* Honeypot: visually hidden, off-screen; real users never fill it. */}
          <input
            type="text" tabIndex={-1} autoComplete="off"
            value={form.website} onChange={set('website')}
            style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            aria-hidden="true"
          />

          {errorMsg && (
            <div style={{
              background: 'rgba(248,81,73,0.12)', border: '1px solid rgba(248,81,73,0.4)',
              borderRadius: 6, padding: '10px 14px', margin: '16px 0 0', color: '#f85149', fontSize: 13,
            }}>{errorMsg}</div>
          )}

          <button
            type="submit"
            disabled={status === 'sending'}
            style={{
              marginTop: 20, background: 'var(--teal)', color: 'var(--bg)', border: 'none',
              padding: '11px 22px', borderRadius: 8, fontSize: 14, fontWeight: 700,
              cursor: status === 'sending' ? 'default' : 'pointer', opacity: status === 'sending' ? 0.7 : 1,
            }}
          >
            {status === 'sending' ? 'Sending…' : 'Send message'}
          </button>
        </form>
      )}

      <p style={{ ...p, marginTop: 32, fontSize: 13 }}>Algovex LLC</p>
    </div>
  );
}
